import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Panel,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  updateWorkflow,
  runN8nWorkflow,
  publishWorkflow,
  unpublishWorkflow,
  API_BASE_URL,
} from '../services/api';
import { toast } from 'react-toastify';
import TriggerNode from '../components/n8n/TriggerNode';
import ApiNode from '../components/n8n/ApiNode';
import RunJsNode from '../components/n8n/RunJsNode';
import AiModelNode from '../components/n8n/AiModelNode';
import AgentNode from '../components/n8n/AgentNode';
import NodeSettingsModal from '../components/n8n/NodeSettingsModal';

const nodeTypes = {
  trigger: TriggerNode,
  api: ApiNode,
  run_js: RunJsNode,
  ai_model: AiModelNode,
  agent: AgentNode,
};

const UNDO_MAX = 15;

const ADDABLE_FRAGMENTS = [
  { type: 'trigger', label: 'Trigger', description: 'Workflow trigger (manual, schedule, webhook)', icon: '▶' },
  { type: 'agent', label: 'Agent', description: 'AI agent with memory and system prompt', icon: '◇' },
  { type: 'ai_model', label: 'AI model', description: 'LLM call to process input', icon: '◆' },
  { type: 'api', label: 'API', description: 'HTTP call to external API', icon: '⎔' },
  { type: 'run_js', label: 'Run JS', description: 'Run JavaScript code', icon: '{}' },
];

function flowToWorkflow(nodes, edges) {
  return {
    nodes: nodes.map((n) => {
      const { __animateAppear, ...dataRest } = n.data ?? {};
      return {
        id: n.id,
        type: n.type,
        position: n.position,
        data: dataRest,
        config: n.data?.config ?? {},
      };
    }),
    edges: edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
    })),
  };
}

function workflowToFlow(w) {
  const nodes = (w.nodes || []).map((n) => ({
    id: n.id,
    type: n.type || 'agent',
    position: n.position || { x: 0, y: 0 },
    data: {
      ...n.data,
      label: n.data?.label ?? n.type,
      config: n.config ?? n.data?.config ?? {},
    },
  }));
  const edges = (w.edges || []).map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle,
    targetHandle: e.targetHandle,
    style: { strokeWidth: 2 },
  }));
  return { nodes, edges };
}

const toolbarStyles = {
  wrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem 0.75rem',
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  section: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#3498db',
    cursor: 'pointer',
    fontSize: '0.9rem',
    padding: '0.35rem 0',
  },
  sep: { width: 1, height: 20, background: 'var(--border-color)', flexShrink: 0 },
  nameInput: {
    fontWeight: 600,
    padding: '0.35rem 0.6rem',
    background: 'var(--card-bg)',
    border: '1px solid #3498db',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    minWidth: 160,
  },
  nameBtn: {
    fontWeight: 600,
    background: 'none',
    border: 'none',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    padding: '0.35rem 0.6rem',
    borderRadius: '6px',
    fontSize: '0.95rem',
  },
  btn: {
    padding: '0.4rem 0.85rem',
    border: '1px solid transparent',
    borderRadius: '6px',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnSecondary: { background: 'var(--card-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' },
  btnRun: { background: '#27ae60' },
  btnPublish: { background: '#3498db' },
  btnUnpublish: { background: 'var(--text-tertiary)', borderColor: 'var(--border-color)' },
  webhookInfo: { fontSize: '0.8rem', color: 'var(--text-tertiary)', marginLeft: 2 },
  webhookLink: { color: '#3498db', textDecoration: 'none' },
};

const sidebarStyles = {
  aside: {
    width: 280,
    flexShrink: 0,
    background: 'var(--bg-primary)',
    borderLeft: '1px solid var(--border-color)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: { padding: '1rem 1.25rem', borderBottom: '1px solid var(--border-color)' },
  title: { margin: 0, fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' },
  subtitle: { margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--text-tertiary)' },
  list: { listStyle: 'none', margin: 0, padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', overflowY: 'auto' },
  listItem: { margin: 0 },
  fragmentBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    background: 'var(--card-bg)',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    color: 'var(--text-primary)',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
  },
  fragmentBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  fragmentIcon: {
    width: 32,
    height: 32,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(52, 152, 219, 0.2)',
    borderRadius: '8px',
    fontSize: '1rem',
    color: '#3498db',
  },
  fragmentContent: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  fragmentLabel: { fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' },
  fragmentDesc: { fontSize: '0.75rem', color: 'var(--text-tertiary)', lineHeight: 1.35 },
};

function EditorInner({ workflowId, workflow: initialWorkflow, onClose, onOpenRunModal }) {
  const [workflow, setWorkflow] = useState(initialWorkflow || null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState(null);
  const [settingsNode, setSettingsNode] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const isRestoringFromHistory = useRef(false);

  useEffect(() => {
    setWorkflow(initialWorkflow || null);
    if (!initialWorkflow) return;
    const { nodes: n, edges: e } = workflowToFlow(initialWorkflow);
    const initialNodes = n.length ? n : [{ id: '1', type: 'trigger', position: { x: 100, y: 100 }, data: { label: 'Trigger' } }];
    setNodes(initialNodes);
    setEdges(e);
    setLastSavedSnapshot(flowToWorkflow(initialNodes, e));
    setNameInput(initialWorkflow.name ?? '');
  }, [initialWorkflow?._id]);

  const pushUndo = useCallback(() => {
    if (isRestoringFromHistory.current) return;
    const snapshot = flowToWorkflow(nodes, edges);
    setUndoStack((prev) => {
      const last = prev[prev.length - 1];
      if (last && JSON.stringify(snapshot) === JSON.stringify(last)) return prev;
      return [...prev, snapshot].slice(-UNDO_MAX);
    });
    setRedoStack([]);
  }, [nodes, edges]);

  const undo = useCallback(() => {
    if (undoStack.length === 0) return;
    isRestoringFromHistory.current = true;
    const snapshot = undoStack[undoStack.length - 1];
    setRedoStack((prev) => [...prev, flowToWorkflow(nodes, edges)]);
    setUndoStack((prev) => prev.slice(0, -1));
    const { nodes: n, edges: e } = workflowToFlow({ nodes: snapshot.nodes, edges: snapshot.edges });
    setNodes(n);
    setEdges(e);
    requestAnimationFrame(() => { isRestoringFromHistory.current = false; });
  }, [undoStack, nodes, edges, setNodes, setEdges]);

  const redo = useCallback(() => {
    if (redoStack.length === 0) return;
    isRestoringFromHistory.current = true;
    const snapshot = redoStack[redoStack.length - 1];
    setUndoStack((prev) => [...prev, flowToWorkflow(nodes, edges)]);
    setRedoStack((prev) => prev.slice(0, -1));
    const { nodes: n, edges: e } = workflowToFlow({ nodes: snapshot.nodes, edges: snapshot.edges });
    setNodes(n);
    setEdges(e);
    requestAnimationFrame(() => { isRestoringFromHistory.current = false; });
  }, [redoStack, nodes, edges, setNodes, setEdges]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo]);

  const onNodesChangeWrapped = useCallback(
    (changes) => {
      const hasRemove = changes.some((c) => c.type === 'remove');
      if (hasRemove) pushUndo();
      onNodesChange(changes);
    },
    [onNodesChange, pushUndo]
  );

  const onEdgesChangeWrapped = useCallback(
    (changes) => {
      const hasRemove = changes.some((c) => c.type === 'remove');
      if (hasRemove) pushUndo();
      onEdgesChange(changes);
    },
    [onEdgesChange, pushUndo]
  );

  const onConnect = useCallback(
    (params) => {
      pushUndo();
      setEdges((eds) => addEdge(params, eds));
    },
    [setEdges, pushUndo]
  );

  const save = useCallback(async () => {
    if (!workflow || !workflowId) return;
    setSaving(true);
    try {
      const snapshot = flowToWorkflow(nodes, edges);
      const updated = await updateWorkflow(workflowId, { ...workflow, nodes: snapshot.nodes, edges: snapshot.edges });
      setWorkflow(updated);
      setLastSavedSnapshot(snapshot);
      toast.success('Uložené');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Chyba pri ukladaní');
    } finally {
      setSaving(false);
    }
  }, [workflowId, workflow, nodes, edges]);

  const currentSnapshot = flowToWorkflow(nodes, edges);
  const hasUnsavedChanges =
    lastSavedSnapshot != null &&
    JSON.stringify(currentSnapshot) !== JSON.stringify(lastSavedSnapshot);

  const run = useCallback(async () => {
    if (!workflowId) return;
    if (onOpenRunModal && workflow) {
      const snapshot = flowToWorkflow(nodes, edges);
      onOpenRunModal({ ...workflow, workflowType: 'n8n', nodes: snapshot.nodes, edges: snapshot.edges });
      return;
    }
    setRunning(true);
    try {
      await runN8nWorkflow(workflowId);
      toast.success('Beh spustený');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Chyba pri spustení');
    } finally {
      setRunning(false);
    }
  }, [workflowId, workflow, nodes, edges, onOpenRunModal]);

  const addNode = useCallback((type) => {
    pushUndo();
    const newId = `n-${Date.now()}`;
    const labels = { agent: 'Agent', api: 'API', ai_model: 'AI model', trigger: 'Trigger', run_js: 'Run JS' };
    setNodes((nds) => [
      ...nds,
      {
        id: newId,
        type,
        position: { x: 250 + Math.random() * 100, y: 150 + Math.random() * 100 },
        data: { label: labels[type] ?? type, config: {}, __animateAppear: true },
      },
    ]);
  }, [pushUndo, setNodes]);

  const onNodeDoubleClick = useCallback((_event, node) => {
    setSettingsNode(node);
  }, []);

  const handleNodeSettingsSave = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, ...newData, config: newData.config ?? n.data?.config } } : n
      )
    );
    setSettingsNode(null);
  }, [setNodes]);

  const startEditingName = useCallback(() => setEditingName(true), []);
  const cancelEditingName = useCallback(() => {
    setEditingName(false);
    setNameInput(workflow?.name ?? '');
  }, [workflow?.name]);

  const saveName = useCallback(async () => {
    if (!workflow || !workflowId) return;
    const trimmed = nameInput.trim();
    if (trimmed === workflow.name) {
      setEditingName(false);
      return;
    }
    if (!trimmed) {
      setEditingName(false);
      setNameInput(workflow.name);
      return;
    }
    setSaving(true);
    try {
      const updated = await updateWorkflow(workflowId, { ...workflow, name: trimmed });
      setWorkflow(updated);
      setEditingName(false);
      toast.success('Názov zmenený');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }, [workflowId, workflow, nameInput]);

  const handleNameKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        saveName();
      }
      if (e.key === 'Escape') cancelEditingName();
    },
    [saveName, cancelEditingName]
  );

  const webhookTrigger = nodes.find(
    (n) => n.type === 'trigger' && (n.data?.config ?? n.data)?.triggerType === 'webhook'
  );
  const webhookConfig = webhookTrigger?.data?.config ?? webhookTrigger?.data ?? {};
  const webhookMethod = (webhookConfig.webhookMethod || 'POST').toUpperCase();
  const hasWebhook = !!webhookTrigger;
  const webhookFullUrl = workflow?.workflowId
    ? `${API_BASE_URL.replace(/\/api\/?$/, '')}/api/automation/trigger/${workflow.workflowId}`
    : '';

  const togglePublish = useCallback(async () => {
    if (!workflow || !workflowId) return;
    const next = !workflow.isPublished;
    setSaving(true);
    try {
      if (next) {
        const res = await publishWorkflow(workflowId);
        setWorkflow((w) => ({ ...w, ...res, isPublished: true, workflowId: res.workflowId ?? w.workflowId }));
        toast.success('Workflow publikovaný – webhook je aktívny.');
      } else {
        const res = await unpublishWorkflow(workflowId);
        setWorkflow((w) => ({ ...w, ...res, isPublished: false, workflowId: null }));
        toast.success('Workflow odpublikovaný.');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Chyba');
    } finally {
      setSaving(false);
    }
  }, [workflowId, workflow?.isPublished]);

  const hasTrigger = nodes.some((n) => n.type === 'trigger');

  if (!workflow) {
    return (
      <div style={{ padding: '2rem', color: 'var(--text-tertiary)' }}>
        Načítavam…
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, minWidth: 0 }} className="workflow-react-flow-dark">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChangeWrapped}
          onEdgesChange={onEdgesChangeWrapped}
          onConnect={onConnect}
          onNodeDragStart={pushUndo}
          onNodeDoubleClick={onNodeDoubleClick}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={{ style: { strokeWidth: 2 } }}
          deleteKeyCode={['Delete', 'Backspace']}
          proOptions={{ hideAttribution: true }}
          fitView
          fitViewOptions={{ maxZoom: 1 }}
        >
          <Background />
          <Controls />
          <MiniMap />
          <Panel position="top-left" style={toolbarStyles.wrapper}>
            <div style={toolbarStyles.section}>
              <button type="button" onClick={onClose} style={toolbarStyles.backBtn}>
                ← Späť na zoznam
              </button>
              <span style={toolbarStyles.sep} />
              {editingName ? (
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={handleNameKeyDown}
                  autoFocus
                  style={toolbarStyles.nameInput}
                />
              ) : (
                <button type="button" onClick={startEditingName} style={toolbarStyles.nameBtn} title="Klik pre zmenu názvu">
                  {workflow.name || 'Workflow'}
                </button>
              )}
            </div>
            <div style={toolbarStyles.section}>
              <button type="button" onClick={undo} disabled={undoStack.length === 0} style={{ ...toolbarStyles.btn, ...toolbarStyles.btnSecondary }} title="Undo (Ctrl+Z)">
                Undo
              </button>
              <button type="button" onClick={redo} disabled={redoStack.length === 0} style={{ ...toolbarStyles.btn, ...toolbarStyles.btnSecondary }} title="Redo (Ctrl+Shift+Z)">
                Redo
              </button>
              <span style={toolbarStyles.sep} />
              <button type="button" onClick={save} disabled={saving || !hasUnsavedChanges} style={toolbarStyles.btn}>
                {saving ? 'Ukladám…' : hasUnsavedChanges ? 'Uložiť' : 'Uložené'}
              </button>
              <button type="button" onClick={run} disabled={running} style={{ ...toolbarStyles.btn, ...toolbarStyles.btnRun }}>
                {running ? 'Spúšťam…' : 'Spustiť'}
              </button>
              {hasWebhook && (
                <>
                  <span style={toolbarStyles.sep} />
                  <button type="button" onClick={togglePublish} disabled={saving} style={{ ...toolbarStyles.btn, ...(workflow?.isPublished ? toolbarStyles.btnUnpublish : toolbarStyles.btnPublish) }} title={workflow?.isPublished ? 'Odpublikovať' : 'Publikovať'}>
                    {workflow?.isPublished ? 'Odpublikovať' : 'Publikovať'}
                  </button>
                  <span style={toolbarStyles.webhookInfo}>
                    {workflow?.isPublished && webhookFullUrl ? (
                      <>
                        {webhookMethod}{' '}
                        <a href={webhookFullUrl} target="_blank" rel="noopener noreferrer" style={toolbarStyles.webhookLink}>
                          {webhookFullUrl}
                        </a>
                      </>
                    ) : (
                      'Webhook po publikovaní'
                    )}
                  </span>
                </>
              )}
            </div>
          </Panel>
        </ReactFlow>
      </div>
      <aside style={sidebarStyles.aside}>
        <div style={sidebarStyles.header}>
          <h2 style={sidebarStyles.title}>Pridať do workflow</h2>
          <p style={sidebarStyles.subtitle}>Kliknite na fragment a pridá sa na plátno</p>
        </div>
        <ul style={sidebarStyles.list}>
          {ADDABLE_FRAGMENTS.map((f) => {
            const disabled = f.type === 'trigger' && hasTrigger;
            return (
              <li key={f.type} style={sidebarStyles.listItem}>
                <button
                  type="button"
                  onClick={() => !disabled && addNode(f.type)}
                  disabled={disabled}
                  title={disabled ? 'Workflow môže mať len jeden trigger' : f.description}
                  style={{ ...sidebarStyles.fragmentBtn, ...(disabled ? sidebarStyles.fragmentBtnDisabled : {}) }}
                >
                  <span style={sidebarStyles.fragmentIcon}>{f.icon}</span>
                  <div style={sidebarStyles.fragmentContent}>
                    <span style={sidebarStyles.fragmentLabel}>{f.label}</span>
                    <span style={sidebarStyles.fragmentDesc}>{f.description}</span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>
      {settingsNode && (
        <NodeSettingsModal
          node={settingsNode}
          onSave={(newData) => handleNodeSettingsSave(settingsNode.id, newData)}
          onClose={() => setSettingsNode(null)}
        />
      )}
    </div>
  );
}

export default function N8nWorkflowEditor({ workflowId, workflow, onClose, onOpenRunModal }) {
  return (
    <ReactFlowProvider>
      <EditorInner workflowId={workflowId} workflow={workflow} onClose={onClose} onOpenRunModal={onOpenRunModal} />
    </ReactFlowProvider>
  );
}
